import { store } from '@wordpress/interactivity';
import { formatPrice, formatPriceSmart, convertStoreApiPrice } from './formatters.js';
import { decodeHTMLEntities, getThumbnailImageUrl } from './dom-utils.js';

function formatVariationValue(rawValue) {
	const decoded = decodeHTMLEntities(rawValue || '').trim();
	if (!decoded) {
		return '';
	}

	// Humanize taxonomy-like slugs: "light-blue" -> "Light Blue"
	if (/^[a-z0-9]+(?:[-_][a-z0-9]+)+$/.test(decoded)) {
		return decoded
			.replace(/[-_]+/g, ' ')
			.replace(/\b[a-z]/g, (char) => char.toUpperCase());
	}

	return decoded;
}

/**
 * Convert Store API cart item to Caddy format with proper price calculations
 *
 * @param {Object} storeApiItem Cart item from WooCommerce Store API
 * @returns {Object} Cart item in Caddy format
 */
export function convertStoreApiItemToCaddyFormat(storeApiItem) {
	const regularPrice = convertStoreApiPrice(storeApiItem.prices?.regular_price);
	const salePrice = convertStoreApiPrice(storeApiItem.prices?.sale_price);

	// Store API totals are always ex-tax; include tax when cart display is tax-inclusive
	let lineTotal = convertStoreApiPrice(storeApiItem.totals?.line_total);
	try {
		const { state: cartState } = store('caddy/cart');
		if (cartState?.taxDisplayCart === 'incl') {
			lineTotal += convertStoreApiPrice(storeApiItem.totals?.line_total_tax || '0');
		}
	} catch (e) {
		// Store not yet initialized
	}

	// Determine if item is on sale: sale price must be less than regular price
	// Store API always returns a sale_price, even when not on sale (it equals regular_price)
	const isOnSale = salePrice < regularPrice;

	// Current price is sale price if on sale, otherwise regular price (per unit)
	const currentUnitPrice = isOnSale ? salePrice : regularPrice;

	// Calculate line totals (unit price × quantity) for display
	let currentLineTotal = currentUnitPrice * storeApiItem.quantity;
	let regularLineTotal = regularPrice * storeApiItem.quantity;
	const saleLineTotal = salePrice * storeApiItem.quantity;

	// Calculate savings percentage based on unit prices
	let savingsPercentage = 0;
	if (isOnSale && regularPrice > 0) {
		savingsPercentage = Math.round(((regularPrice - salePrice) / regularPrice) * 100);
	}

	// Extract variation attributes from Store API data
	let variationText = '';
	if (storeApiItem.variation && Array.isArray(storeApiItem.variation)) {
		variationText = storeApiItem.variation
			.map((attr) => formatVariationValue(attr.value))
			.filter(Boolean)
			.join(', ');
	} else if (storeApiItem.item_data && Array.isArray(storeApiItem.item_data)) {
		variationText = storeApiItem.item_data
			.map((attr) => formatVariationValue(attr.display || attr.value))
			.filter(Boolean)
			.join(', ');
	}

	// Decode HTML entities in product name to prevent double encoding
	const decodedName = decodeHTMLEntities(storeApiItem.name);

	// Check if this is a free gift from rewards
	// Extensions are namespaced, so it's in extensions.caddy.caddy_free_gift
	const isFreeGift = !!(
		storeApiItem.extensions?.caddy?.caddy_free_gift === true
	);

	// Check bundle status using WooCommerce Product Bundles extension data
	// Bundle containers have type === 'bundle'
	// Bundled items have extensions.bundles.bundled_by (parent cart key)
	const isBundleContainer = storeApiItem.type === 'bundle';
	const isBundledItem = !!(storeApiItem.extensions?.bundles?.bundled_by);
	const bundledBy = storeApiItem.extensions?.bundles?.bundled_by || null;

	// Build item class string (computed after quantity limits below, but declared here)
	let itemClass = 'cc-cart-product-list cc-cart-item';
	if (isBundleContainer) {
		itemClass += ' bundle';
	}
	if (isBundledItem) {
		itemClass += ' bundled_child';
	}

	// Extract quantity limits from Store API (handles sold_individually, min/max qty)
	const quantityLimits = storeApiItem.quantity_limits || {};
	const maxQuantity = quantityLimits.maximum || Infinity;
	const minQuantity = quantityLimits.minimum || 1;
	const soldIndividually = maxQuantity === 1;

	// Bundled items with variable qty (min != max) allow quantity changes
	const bundledHasVariableQty = isBundledItem && minQuantity < maxQuantity;

	// Compute display flags for bundle/gift items
	const shouldHideControls = isFreeGift || isBundledItem; // Hide remove & SFL buttons
	const hideQuantity = isFreeGift; // Only free gifts hide qty entirely
	const hideQuantityButtons = isBundledItem && !bundledHasVariableQty; // Hide +/- for fixed-qty bundled items

	const hidePrice = !isBundleContainer && currentLineTotal === 0 && regularLineTotal === 0;

	// Add CSS class for fixed-qty bundled items (CSS hides +/- buttons)
	if (isBundledItem && !bundledHasVariableQty) {
		itemClass += ' bundled_fixed_qty';
	}

	return {
		cartKey: storeApiItem.key,
		productId: storeApiItem.id,
		quantity: storeApiItem.quantity,
		name: decodedName,
		variationText: variationText, // Add variation attributes
		price: formatPriceSmart(currentLineTotal), // Current effective line total (formatted number only)
		priceHtml: formatPrice(currentLineTotal), // Full formatted price with symbol and position
		regularPrice: regularPrice, // Regular UNIT price (numeric for calculations)
		regularLineTotal: regularLineTotal, // Regular line total (numeric)
		regularPriceFormatted: formatPriceSmart(regularLineTotal), // Regular line total (formatted number only)
		regularPriceHtml: isOnSale ? formatPrice(regularLineTotal) : '', // Full formatted regular price
		salePrice: formatPriceSmart(saleLineTotal), // Sale line total (formatted)
		unitPrice: currentUnitPrice, // Unit price for reference
		isOnSale: isOnSale, // Boolean: is this item on sale?
		savingsPercentage: savingsPercentage, // Percentage saved
		lineTotal: lineTotal, // Store API line total (should match currentLineTotal)
		lineTotalFormatted: storeApiItem.totals?.line_total_formatted || formatPrice(lineTotal),
		image: getThumbnailImageUrl(storeApiItem.images?.[0]?.thumbnail || storeApiItem.images?.[0]?.src),
		permalink: storeApiItem.permalink || `${window.location.origin}/?p=${storeApiItem.id}`,
		isFreeGift: isFreeGift, // Boolean: is this a free gift reward?
		isBundleContainer: isBundleContainer, // Boolean: is this a bundle container?
		isBundledItem: isBundledItem, // Boolean: is this a bundled item?
		bundledBy: bundledBy, // Parent bundle cart key (if bundled item)
		shouldHideControls: shouldHideControls, // Boolean: should hide save/remove controls
		hideQuantity: hideQuantity, // Boolean: hide qty display entirely (free gifts only)
		hideQuantityButtons: hideQuantityButtons, // Boolean: hide +/- buttons (fixed-qty bundled items)
		hidePrice: hidePrice, // Boolean: hide price when $0
		itemClass: itemClass, // Pre-computed class string
		// Add computed flags for template conditional rendering
		showSalePrice: isOnSale, // Only show crossed-out price if actually on sale
		showSavings: isOnSale && savingsPercentage > 0, // Only show savings if on sale and has savings
		soldIndividually: soldIndividually,
		maxQuantity: maxQuantity,
		minQuantity: minQuantity,
		isAtMinQty: storeApiItem.quantity <= minQuantity,
		isAtMaxQty: storeApiItem.quantity >= maxQuantity,
		// WCS-ATT: subscriptionOptions must be an array for data-wp-each.
		// hasSubscriptionSchemes left undefined to signal "not yet hydrated from server".
		subscriptionOptions: []
	};
}

/**
 * Convert all Store API cart items and aggregate bundle container prices from children
 */
export function convertCartItems(storeApiItems) {
	const items = storeApiItems.map(item => convertStoreApiItemToCaddyFormat(item));
	// For per-item-priced bundle containers showing $0, sum children's prices
	for (const item of items) {
		if (item.isBundleContainer && parseFloat(item.price) === 0) {
			const children = items.filter(child => child.bundledBy === item.cartKey);
			if (children.length > 0) {
				const aggTotal = children.reduce((sum, child) => sum + (parseFloat(child.price) || 0), 0);
				if (aggTotal > 0) {
					item.price = formatPriceSmart(aggTotal);
					item.priceHtml = formatPrice(aggTotal);
					item.regularLineTotal = aggTotal;
					item.regularPriceFormatted = formatPriceSmart(aggTotal);
					item.lineTotal = aggTotal;
					item.lineTotalFormatted = formatPrice(aggTotal);
				}
			}
		}
	}
	return items;
}
